// src/stations/scanner/scannerStation.ts — Schoolwork Scanner Station (T-6.1.5, FR-SCAN-001..003)
// Orchestrates the complete schoolwork-to-remediation workflow:
// 1. Upload/Capture Stage: accepts worksheets (image or text)
// 2. Diagnosis Stage: displays KS2 misconception codes, error patterns & Socratic insight
// 3. Remediation Quest: 5 targeted marine drill questions with live feedback & hints
// 4. Results & Mastery: score summary, Explorer Points awarded, and scan history linkage

import { tokens } from '@ui/designTokens';
import { soundEngine } from '@audio/soundEngine';
import { AppShell } from '@ui/shell';
import { SchoolworkUploader, type UploadPayload } from './uploader';
import { MisconceptionAnalyzer } from './misconceptionAnalyzer';
import { AdaptiveQuestGenerator } from './adaptiveQuestGenerator';
import {
  ScannerStorage,
  type SchoolworkScanRecord,
  type RemediationResult,
} from '@storage/scannerStorage';
import { StationProgression } from '@storage/stationProgression';
import { ExplorerPointsManager } from '@storage/explorerPoints';
import { StreakManager } from '@storage/streakManager';
import type { ClaweyHud } from '@ui/components/claweyHud';

export type ScannerStationState = 'upload' | 'diagnosis' | 'remediation_active' | 'results';

export interface ScannerStationOptions {
  hud?: ClaweyHud;
}

export class ScannerStation {
  private container: HTMLElement;
  private hud?: ClaweyHud;
  private state: ScannerStationState = 'upload';

  // Current session data
  private currentScan: SchoolworkScanRecord | null = null;
  private currentQuestionIndex: number = 0;
  private currentAnswers: Record<string, number | null> = {};
  private hintRevealed: Record<string, boolean> = {};

  constructor(options: ScannerStationOptions = {}) {
    this.container = document.createElement('div');
    this.container.className = 'scanner-station-view';
    this.hud = options.hud;
    this.render();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  getState(): ScannerStationState {
    return this.state;
  }

  getCurrentScan(): SchoolworkScanRecord | null {
    return this.currentScan;
  }

  render(): HTMLElement {
    this.container.innerHTML = '';

    if (this.state === 'upload') {
      this.renderUploadStage();
    } else if (this.state === 'diagnosis') {
      this.renderDiagnosisStage();
    } else if (this.state === 'remediation_active') {
      this.renderRemediationStage();
    } else if (this.state === 'results') {
      this.renderResultsStage();
    }

    return this.container;
  }

  /** Stage 1: Upload / Capture Stage */
  private renderUploadStage(): void {
    AppShell.setFocusMode(false);

    const scans = ScannerStorage.loadScans();

    const uploader = new SchoolworkUploader({
      onUploadSuccess: (payload) => {
        this.processUploadedSchoolwork(payload);
      },
    });

    const wrapper = document.createElement('div');
    wrapper.appendChild(uploader.getElement());

    // Historical Scans Section
    if (scans.length > 0) {
      const historyCard = document.createElement('section');
      historyCard.className = 'morris-card morris-card-gold';
      historyCard.style.maxWidth = '800px';
      historyCard.style.margin = '28px auto 0';
      historyCard.style.padding = '24px 28px';

      historyCard.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
          <h3 style="font-family: ${tokens.font.heading}; font-size: 1.35rem; color: ${tokens.color.morrisIndigo}; margin: 0;">
            📚 Tudor's Previous Scans & Worksheets
          </h3>
          <span style="font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage};">
            ${scans.length} Scan${scans.length > 1 ? 's' : ''} on record
          </span>
        </div>

        <div style="display: flex; flex-direction: column; gap: 12px;">
          ${scans.slice(0, 4).map((s) => {
            const hasScore = s.remediationResult !== undefined;
            return `
              <div
                class="scan-history-row"
                data-scan-id="${s.id}"
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  padding: 12px 18px;
                  background: rgba(255, 253, 249, 0.95);
                  border: 1.5px solid rgba(26, 54, 93, 0.15);
                  border-radius: 12px;
                  cursor: pointer;
                  transition: all 0.15s ease;
                "
              >
                <div>
                  <div style="font-weight: 800; color: ${tokens.color.morrisIndigo}; font-size: 1rem;">
                    ${s.title}
                  </div>
                  <div style="font-size: 0.8rem; color: #5A6D86; margin-top: 2px;">
                    ${s.dateFormatted} · ${s.diagnosedMisconceptions.map((m) => m.ks2Code).join(', ')}
                  </div>
                </div>

                <div style="display: flex; align-items: center; gap: 12px;">
                  ${hasScore ? `
                    <span style="font-size: 0.85rem; font-weight: 800; padding: 4px 10px; border-radius: 8px; background: rgba(61, 90, 69, 0.15); color: ${tokens.color.seaweedSage};">
                      ⭐ ${s.remediationResult?.correctCount} / 5 Correct
                    </span>
                  ` : `
                    <span style="font-size: 0.85rem; font-weight: 700; padding: 4px 10px; border-radius: 8px; background: rgba(229, 169, 60, 0.2); color: ${tokens.color.morrisIndigo};">
                      Remediation Pending
                    </span>
                  `}
                  <button
                    type="button"
                    class="touch-target btn-resume-scan"
                    data-scan-id="${s.id}"
                    style="padding: 6px 14px; font-size: 0.85rem; border: 1px solid ${tokens.color.morrisIndigo}; border-radius: 8px; background: #FFFDF9; cursor: pointer; font-weight: 700;"
                  >
                    Open &rarr;
                  </button>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `;

      wrapper.appendChild(historyCard);
    }

    this.container.appendChild(wrapper);

    // Event listeners for history rows
    const resumeBtns = this.container.querySelectorAll<HTMLButtonElement>('.btn-resume-scan');
    resumeBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const scanId = btn.getAttribute('data-scan-id');
        if (scanId) {
          const scan = ScannerStorage.getScan(scanId);
          if (scan) {
            soundEngine.playTap();
            this.currentScan = scan;
            this.state = 'diagnosis';
            this.render();
          }
        }
      });
    });

    if (this.hud) {
      this.hud.say('Upload a school worksheet, Tudor! Let us discover where Clawey can help.', 'thinking');
    }
  }

  /** Analyze uploaded payload and advance to Diagnosis stage */
  processUploadedSchoolwork(payload: UploadPayload): void {
    const scanId = `scan_${Date.now()}`;
    const now = new Date();
    const dateFormatted = now.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    // 1. Diagnose Misconceptions (FR-SCAN-001)
    const misconceptions = MisconceptionAnalyzer.analyze(payload);

    // 2. Generate exactly 5 adaptive remediation questions (FR-SCAN-002)
    const questions = AdaptiveQuestGenerator.generate(misconceptions, scanId);

    // 3. Assemble and persist record (FR-SCAN-003)
    const scanRecord: SchoolworkScanRecord = {
      id: scanId,
      createdAt: now.getTime(),
      dateFormatted,
      title: payload.title,
      sourceType: payload.sourceType,
      fileName: payload.fileName,
      fileSizeBytes: payload.fileSizeBytes,
      imageDataUrl: payload.imageDataUrl,
      rawTextContent: payload.rawTextContent,
      diagnosedMisconceptions: misconceptions,
      remediationQuestions: questions,
    };

    ScannerStorage.saveScan(scanRecord);
    this.currentScan = scanRecord;
    this.state = 'diagnosis';
    this.render();

    if (this.hud) {
      const primaryCode = misconceptions[0]?.ks2Code || 'KS2';
      this.hud.say(
        `Worksheet analyzed! I have mapped the error pattern to KS2 Objective [${primaryCode}]. Ready to practice!`,
        'celebrating'
      );
    }
  }

  /** Stage 2: Misconception Diagnosis Card */
  private renderDiagnosisStage(): void {
    AppShell.setFocusMode(false);
    if (!this.currentScan) return;

    const scan = this.currentScan;

    this.container.innerHTML = `
      <section class="morris-card morris-card-gold" style="max-width: 820px; margin: 20px auto; padding: 36px 32px; box-shadow: 0 8px 32px rgba(10, 25, 47, 0.14);">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 14px; margin-bottom: 20px;">
          <div>
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              Diagnosed Learning Gap · ${scan.dateFormatted}
            </div>
            <h1 style="font-family: ${tokens.font.heading}; font-size: 2.2rem; color: ${tokens.color.morrisIndigo}; margin: 4px 0 8px;">
              ${scan.title}
            </h1>
            <p style="font-size: 1rem; color: #3A4D66; margin: 0;">
              Clawey has parsed this schoolwork sample and identified the key misconception pattern to target.
            </p>
          </div>
          <span style="font-size: 3rem;">🔍</span>
        </div>

        <!-- Optional Image Thumbnail Preview -->
        ${scan.imageDataUrl ? `
          <div style="margin-bottom: 22px; text-align: center;">
            <img src="${scan.imageDataUrl}" alt="Worksheet Preview" style="max-height: 220px; max-width: 100%; border-radius: 12px; border: 2px solid ${tokens.color.gildedBrass}; box-shadow: 0 4px 16px rgba(0,0,0,0.1);" />
          </div>
        ` : ''}

        <!-- Diagnosed Misconceptions List -->
        <div style="display: flex; flex-direction: column; gap: 16px; margin-bottom: 28px;">
          ${scan.diagnosedMisconceptions.map((m) => `
            <div style="padding: 22px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.morrisIndigo}; border-radius: 14px;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <span style="font-size: 0.85rem; font-weight: 800; padding: 4px 12px; border-radius: 20px; background: rgba(26, 54, 93, 0.12); color: ${tokens.color.morrisIndigo};">
                  KS2 Objective: ${m.ks2Code} · ${m.ks2Domain}
                </span>
                <span style="font-weight: 700; color: ${tokens.color.madderCoral}; font-size: 0.85rem;">
                  Pattern Diagnosed
                </span>
              </div>

              <h3 style="font-size: 1.3rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 8px;">
                ${m.title}
              </h3>

              <div style="margin-bottom: 12px; padding: 12px 16px; background: rgba(255, 107, 74, 0.08); border-left: 3px solid ${tokens.color.madderCoral}; border-radius: 8px; font-size: 0.95rem; color: #2D3E50;">
                <strong>What happened:</strong> ${m.errorPattern}
              </div>

              <div style="padding: 12px 16px; background: rgba(229, 169, 60, 0.15); border-left: 3px solid ${tokens.color.gildedBrass}; border-radius: 8px; font-size: 0.95rem; color: ${tokens.color.morrisIndigo}; line-height: 1.5;">
                <strong>Clawey's Socratic Insight:</strong> ${m.socraticInsight}
              </div>
            </div>
          `).join('')}
        </div>

        <!-- 5-Question Remediation Banner -->
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; padding: 18px 24px; background: rgba(255, 246, 229, 0.9); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 14px; margin-bottom: 28px;">
          <div>
            <div style="font-weight: 800; color: ${tokens.color.morrisIndigo}; font-size: 1.1rem;">
              🎯 5-Question Adaptive Marine Drill Ready
            </div>
            <div style="font-size: 0.9rem; color: #5A6D86; margin-top: 2px;">
              Targeted practice designed to replace this misconception with deep conceptual understanding.
            </div>
          </div>
          <button
            id="btn-start-remediation"
            class="btn-primary touch-target"
            style="padding: 14px 32px; font-size: 1.15rem; font-weight: 800;"
          >
            Launch 5-Question Drill &rarr;
          </button>
        </div>

        <!-- Action Links -->
        <div style="display: flex; gap: 14px; justify-content: flex-start;">
          <button
            id="btn-new-scan"
            type="button"
            class="touch-target"
            style="padding: 10px 18px; font-size: 0.95rem; font-weight: 700; border: 1.5px solid rgba(26,54,93,0.3); border-radius: 12px; background: #FFFDF9; cursor: pointer;"
          >
            &larr; Upload Another Worksheet
          </button>
        </div>
      </section>
    `;

    const startBtn = this.container.querySelector<HTMLButtonElement>('#btn-start-remediation');
    startBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.startRemediation();
    });

    const newScanBtn = this.container.querySelector<HTMLButtonElement>('#btn-new-scan');
    newScanBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.state = 'upload';
      this.render();
    });
  }

  /** Stage 3: Remediation Quest Drill */
  private startRemediation(): void {
    if (!this.currentScan) return;
    this.state = 'remediation_active';
    this.currentQuestionIndex = 0;
    this.currentAnswers = {};
    this.hintRevealed = {};
    this.render();
  }

  private renderRemediationStage(): void {
    AppShell.setFocusMode(true);
    if (!this.currentScan) return;

    const questions = this.currentScan.remediationQuestions;
    const q = questions[this.currentQuestionIndex];
    if (!q) return;

    const selectedOption = this.currentAnswers[q.id];
    const letters = ['A', 'B', 'C', 'D'];
    const isAnswered = selectedOption !== undefined && selectedOption !== null;
    const isCorrect = isAnswered && selectedOption === q.correctOptionIndex;
    const showHint = !!this.hintRevealed[q.id];

    this.container.innerHTML = `
      <div style="max-width: 780px; margin: 16px auto;">
        <!-- Top Status Bar -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding: 12px 18px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.morrisIndigo}; border-radius: 14px;">
          <div>
            <span style="font-weight: 800; color: ${tokens.color.morrisIndigo}; font-size: 1.05rem;">
              Adaptive Drill: ${q.ks2Code} · ${q.topic}
            </span>
          </div>

          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="font-weight: 800; color: ${tokens.color.seaweedSage}; font-size: 0.95rem;">
              Question ${this.currentQuestionIndex + 1} of ${questions.length}
            </span>
          </div>
        </div>

        <!-- Question Card -->
        <section class="morris-card" style="padding: 32px 28px; position: relative;">
          <h2 style="font-size: 1.35rem; line-height: 1.5; color: ${tokens.color.morrisIndigo}; margin: 0 0 16px;">
            ${q.prompt}
          </h2>

          ${q.contextText ? `
            <div style="padding: 14px 18px; background: rgba(255, 246, 229, 0.7); border-left: 3px solid ${tokens.color.gildedBrass}; margin-bottom: 20px; font-size: 0.95rem; color: #2D3E50; font-style: italic;">
              ${q.contextText}
            </div>
          ` : ''}

          <!-- Options Grid -->
          <div style="display: flex; flex-direction: column; gap: 12px; margin-top: 16px;">
            ${q.options.map((opt, idx) => {
              const isSelected = selectedOption === idx;
              let bg = '#FFFDF9';
              let border = 'rgba(26, 54, 93, 0.15)';
              const color = tokens.color.morrisIndigo;

              if (isAnswered) {
                if (idx === q.correctOptionIndex) {
                  bg = 'rgba(61, 90, 69, 0.15)';
                  border = tokens.color.seaweedSage;
                } else if (isSelected && !isCorrect) {
                  bg = 'rgba(255, 107, 74, 0.15)';
                  border = tokens.color.madderCoral;
                }
              } else if (isSelected) {
                bg = 'rgba(255, 246, 229, 0.95)';
                border = tokens.color.morrisIndigo;
              }

              return `
                <button
                  type="button"
                  class="remediation-option-btn touch-target"
                  data-option-idx="${idx}"
                  style="
                    padding: 16px 20px;
                    text-align: left;
                    font-size: 1.05rem;
                    font-weight: 700;
                    border-radius: 12px;
                    border: 2px solid ${border};
                    background: ${bg};
                    color: ${color};
                    cursor: ${isAnswered ? 'default' : 'pointer'};
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    min-height: 52px;
                    transition: all 0.15s ease;
                  "
                  ${isAnswered ? 'disabled' : ''}
                >
                  <span style="
                    display: inline-flex;
                    justify-content: center;
                    align-items: center;
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    background: ${isSelected ? tokens.color.morrisIndigo : 'rgba(26, 54, 93, 0.08)'};
                    color: ${isSelected ? '#FFFDF9' : tokens.color.morrisIndigo};
                    font-weight: 800;
                    font-size: 0.9rem;
                    flex-shrink: 0;
                  ">
                    ${letters[idx]}
                  </span>
                  <span>${opt}</span>
                </button>
              `;
            }).join('')}
          </div>

          <!-- Clawey Socratic Hint Toggle -->
          <div style="margin-top: 20px;">
            ${showHint ? `
              <div style="padding: 14px 18px; background: rgba(0, 229, 255, 0.1); border-left: 3px solid ${tokens.color.biolumCyan}; border-radius: 10px; font-size: 0.95rem; color: ${tokens.color.morrisIndigo}; line-height: 1.5;">
                💡 <strong>Clawey's Socratic Hint:</strong> ${q.socraticHint}
              </div>
            ` : `
              <button
                id="btn-reveal-hint"
                type="button"
                class="touch-target"
                style="padding: 8px 16px; font-size: 0.9rem; font-weight: 800; border: 1.5px dashed ${tokens.color.gildedBrass}; border-radius: 12px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer;"
              >
                💡 Ask Clawey for a Socratic Hint (+3 EP)
              </button>
            `}
          </div>

          <!-- Explanation feedback on answer -->
          ${isAnswered ? `
            <div style="margin-top: 22px; padding: 16px 20px; border-radius: 12px; background: ${isCorrect ? 'rgba(61, 90, 69, 0.12)' : 'rgba(255, 107, 74, 0.12)'}; border: 1.5px solid ${isCorrect ? tokens.color.seaweedSage : tokens.color.madderCoral};">
              <div style="font-weight: 800; font-size: 1.05rem; color: ${isCorrect ? tokens.color.seaweedSage : tokens.color.madderCoral}; margin-bottom: 6px;">
                ${isCorrect ? '✅ Brilliant deduction, Tudor!' : '🧭 Let\'s reflect together:'}
              </div>
              <p style="margin: 0; font-size: 0.95rem; color: #2D3E50; line-height: 1.5;">
                ${q.explanation}
              </p>
            </div>
          ` : ''}

          <!-- Bottom Navigation Pager -->
          <div style="display: flex; justify-content: flex-end; margin-top: 24px; padding-top: 18px; border-top: 1.5px solid rgba(26,54,93,0.1);">
            <button
              id="btn-remediation-next"
              type="button"
              class="btn-primary touch-target"
              style="padding: 12px 28px; font-size: 1rem; font-weight: 800;"
              ${!isAnswered ? 'disabled style="opacity: 0.5;"' : ''}
            >
              ${this.currentQuestionIndex < questions.length - 1 ? 'Next Question &rarr;' : 'Finish Remediation Drill &rarr;'}
            </button>
          </div>
        </section>
      </div>
    `;

    this.attachRemediationListeners();
  }

  private attachRemediationListeners(): void {
    if (!this.currentScan) return;
    const questions = this.currentScan.remediationQuestions;
    const q = questions[this.currentQuestionIndex];
    if (!q) return;

    // Option clicks
    const optionBtns = this.container.querySelectorAll<HTMLButtonElement>('.remediation-option-btn');
    optionBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const optIdx = Number(btn.getAttribute('data-option-idx'));
        this.currentAnswers[q.id] = optIdx;

        const isCorrect = optIdx === q.correctOptionIndex;
        if (isCorrect) {
          soundEngine.playSuccess();
          if (this.hud) {
            this.hud.say('Superb work, Tudor! You nailed this concept.', 'celebrating');
          }
        } else {
          soundEngine.playTap();
          if (this.hud) {
            this.hud.say('Steady on! Inspect Clawey’s explanation below.', 'thinking');
          }
        }
        this.render();
      });
    });

    // Reveal Hint button
    const hintBtn = this.container.querySelector<HTMLButtonElement>('#btn-reveal-hint');
    hintBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.hintRevealed[q.id] = true;
      ExplorerPointsManager.award('hint_then_correct', q.id);
      this.render();
    });

    // Next / Finish button
    const nextBtn = this.container.querySelector<HTMLButtonElement>('#btn-remediation-next');
    nextBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      if (this.currentQuestionIndex < questions.length - 1) {
        this.currentQuestionIndex += 1;
        this.render();
      } else {
        this.finishRemediation();
      }
    });
  }

  /** Complete remediation quest and compute results (FR-SCAN-003) */
  private finishRemediation(): void {
    if (!this.currentScan) return;

    const questions = this.currentScan.remediationQuestions;
    let correctCount = 0;

    for (const q of questions) {
      if (this.currentAnswers[q.id] === q.correctOptionIndex) {
        correctCount += 1;
      }
    }

    const earnedEp = 10 + correctCount * 2;
    ExplorerPointsManager.award('harder_tier'); // Award +2 effort points

    const result: RemediationResult = {
      totalQuestions: 5,
      correctCount,
      answers: { ...this.currentAnswers },
      completedAt: Date.now(),
      earnedEp,
    };

    // Update scan in storage with scanId foreign key linkage (FR-SCAN-003)
    ScannerStorage.updateRemediationResult(this.currentScan.id, result);
    this.currentScan.remediationResult = result;

    // Record progress in 10-tier StationProgression engine (FR-PROG-001)
    const prog = StationProgression.getProgression('scan');
    const setNum = prog.currentSet;
    StationProgression.completeSet('scan', setNum, correctCount, 5);

    StreakManager.recordActivity();
    this.state = 'results';
    this.render();

    if (this.hud) {
      this.hud.say(
        `Remediation complete! You scored ${correctCount} of 5 and earned +${earnedEp} Explorer Points!`,
        'celebrating'
      );
    }
  }

  /** Stage 4: Results & Mastery Screen */
  private renderResultsStage(): void {
    AppShell.setFocusMode(false);
    const scan = this.currentScan;
    if (!scan || !scan.remediationResult) return;

    const res = scan.remediationResult;
    const percent = Math.round((res.correctCount / 5) * 100);
    const sealData = StationProgression.getStationMasterySeal('scan');
    const prog = StationProgression.getProgression('scan');
    const currentSetRecord = prog.completedSets[prog.currentSet];
    const stars = currentSetRecord ? currentSetRecord.stars : res.correctCount >= 5 ? 3 : res.correctCount >= 4 ? 2 : 1;
    const sealTier = currentSetRecord ? currentSetRecord.masterySeal : 'bronze';
    const sealIcon = sealTier === 'gold' ? '🥇' : sealTier === 'silver' ? '🥈' : '🥉';
    const sealColor = sealTier === 'gold' ? '#D4AF37' : sealTier === 'silver' ? '#A0AEC0' : '#CD7F32';

    this.container.innerHTML = `
      <section class="morris-card morris-card-gold" style="max-width: 740px; margin: 24px auto; padding: 40px 32px; text-align: center; box-shadow: 0 8px 32px rgba(10, 25, 47, 0.15);">
        <div style="font-size: 3.5rem; margin-bottom: 8px;">🦀 📜 🌟</div>

        <div style="display: flex; justify-content: center; gap: 8px; align-items: center; margin-bottom: 8px;">
          <span style="font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
            Remediation Quest Concluded
          </span>
          <span style="
            font-size: 0.75rem;
            font-weight: 800;
            padding: 3px 10px;
            border-radius: 999px;
            background: rgba(255, 246, 229, 0.95);
            border: 1px solid ${sealColor};
            color: #4A3A16;
          ">
            ${sealIcon} ${sealTier.toUpperCase()} SEAL
          </span>
        </div>

        <h1 style="font-family: ${tokens.font.heading}; font-size: 2.4rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 6px;">
          Misconception Conquered!
        </h1>

        <div style="font-size: 1.2rem; margin-bottom: 14px;" title="${stars} Star Mastery">
          ${'⭐'.repeat(stars)}
        </div>

        <p style="font-size: 1.05rem; color: #3A4D66; max-width: 520px; margin: 0 auto 28px; line-height: 1.5;">
          Tudor targeted the gaps diagnosed in <em>${scan.title}</em> with 5 custom marine practice drills.
        </p>

        <!-- Scoreboard Stat Boxes -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-bottom: 32px;">
          <div style="padding: 20px; background: rgba(255, 253, 249, 0.95); border: 2px solid ${tokens.color.gildedBrass}; border-radius: 14px;">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              Remediation Accuracy
            </div>
            <div style="font-family: ${tokens.font.heading}; font-size: 2.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin: 4px 0;">
              ${res.correctCount} / 5
            </div>
            <div style="font-size: 0.85rem; color: #5A6D86;">${percent}% Correct</div>
          </div>

          <div style="padding: 20px; background: rgba(255, 246, 229, 0.95); border: 2px solid ${tokens.color.gildedBrass}; border-radius: 14px;">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.gildedBrass}; text-transform: uppercase;">
              Explorer Points Earned
            </div>
            <div style="font-family: ${tokens.font.heading}; font-size: 2.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin: 4px 0;">
              +${res.earnedEp}
            </div>
            <div style="font-size: 0.85rem; color: #5A6D86;">Mastered Sets: ${sealData.completedCount} of 10</div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div style="display: flex; justify-content: center; gap: 14px; flex-wrap: wrap;">
          <button id="btn-results-retry" class="btn-primary touch-target" style="padding: 14px 28px; font-size: 1.05rem;">
            🔄 Retry This 5-Question Drill
          </button>
          <button id="btn-results-new-scan" class="touch-target" style="padding: 14px 24px; font-size: 1rem; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 14px; color: ${tokens.color.morrisIndigo}; font-weight: 800; cursor: pointer;">
            📷 Upload Another Worksheet
          </button>
          <a href="#/" class="touch-target" style="padding: 14px 24px; font-size: 1rem; border: 1.5px solid rgba(26,54,93,0.3); background: #FFFDF9; border-radius: 14px; text-decoration: none; color: ${tokens.color.morrisIndigo}; font-weight: 700; display: inline-flex; align-items: center;">
            🗺️ Back to Map
          </a>
        </div>
      </section>
    `;

    const retryBtn = this.container.querySelector<HTMLButtonElement>('#btn-results-retry');
    retryBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.startRemediation();
    });

    const newScanBtn = this.container.querySelector<HTMLButtonElement>('#btn-results-new-scan');
    newScanBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.state = 'upload';
      this.currentScan = null;
      this.render();
    });
  }
}
